import React, {useEffect, useState} from 'react';
import {useHistory} from "react-router";
import styled from 'styled-components';

import {useActionApi, useApi, usePersistedState} from "hooks";
import {entities} from "reduxStore";
import Api from "api";

import {BackendJob, EditedJob, createEditedJob} from "components/EditJob";
import {TopBarRedesigned} from "components/TopBarRedesigned";
import {JobBoardDetails, Application} from "components/JobBoardDetails";
import {Location} from "components/BillingLocationSelector";
import Popup from 'components/Popup'
import FixedLoader from 'components/FixedLoader'
import FixedError from 'components/FixedError'
import {AdditionalActions} from 'components/JobBoardDetails/AdditionalActions';
import {DeleteJobConfirmation} from 'components/JobBoardDetails/DeleteJobConfirmation'

const PageWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  background-color: #fff;
  overflow: auto;
  position: relative;
  min-height: 100vh;
`;

const JobBoardDetailsPage = () => {
    const history = useHistory();

    const [confirmDeletePopupOpened, setConfirmDeletePopupOpened] = useState(false);
    const [additionalActionsPopupOpened, setAdditionalActionsPopupOpened] = useState(false);

    const { job } = history.location.state as { job: BackendJob }

    const billingLocations = usePersistedState({
        entityName: entities.dentistBillingLocations,
        initialValue: [],
    })[0] as Location[]

    const setJobToEdit = usePersistedState({
        entityName: entities.editedJobPost,
        initialValue: job as EditedJob,
    })[1]

    const setApplicationToView = usePersistedState({
        entityName: entities.dentistCandidateApplication,
    })[1]

    const [jobCandidates, loading, error, getCandidates] = useApi({
        apiMethod: Api.getApplicationsForHireJob,
        initialValue: [],
    })

    const [loadingDelete, errorDelete, deleteJob] = useActionApi({
        apiMethod: Api.deleteJob
    })

    useEffect(() => {
        getCandidates(job.id as number)
    }, [job.id, getCandidates])

    const onApplicationClick = (application: Application) => {
        setApplicationToView(application)
        history.push({
            pathname: 'candidate',
            state: {
                job,
            },
        })
    }

    const onJobEditClick = () => {
        setAdditionalActionsPopupOpened(false);
        setJobToEdit(createEditedJob(job));

        history.push({
            pathname: "editJobOverviewStep",
            state: { mode: "edit", originalJob: job }
        })
    }

    const onJobDeleteClick = () => {
        setAdditionalActionsPopupOpened(false);
        setConfirmDeletePopupOpened(true)
    }

    const onJobDeleteConfirmClick = () => {
        if (job.status === 0) {
            deleteJob(job.id as number)

            history.push({
                pathname: 'deleteSuccess'
            })
        } else {
            history.push({
                pathname: 'jobFeedback',
                state: {
                    job,
                },
            })
        }
    }

    return (
        <PageWrapper>
            <TopBarRedesigned
                title='Job Details'
                onBack={() => history.replace('jobBoard')}
                onAdditionalActionsButtonClick={() => setAdditionalActionsPopupOpened(true)}
            />
            <JobBoardDetails
                job={job}
                locations={billingLocations}
                applications={jobCandidates}
                onApplicationClick={onApplicationClick}
            />
            <Popup
                opened={additionalActionsPopupOpened}
                onClose={() => setAdditionalActionsPopupOpened(false)}
            >
                <AdditionalActions
                    onEdit={onJobEditClick}
                    onDelete={onJobDeleteClick}
                />
            </Popup>
            <Popup
                opened={confirmDeletePopupOpened}
                onClose={() => setConfirmDeletePopupOpened(false)}
            >
                <DeleteJobConfirmation
                    hardDelete={job.status === 0}
                    onCancel={() => setConfirmDeletePopupOpened(false)}
                    onDelete={onJobDeleteConfirmClick}
                />
            </Popup>
            <FixedLoader loading={loading || loadingDelete} />
            <FixedError error={error || errorDelete} />
        </PageWrapper>
    )
}

export default JobBoardDetailsPage;
